import React, { useEffect, useState } from 'react';
import { ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/24/outline';

interface DataCollectionModalProps {
  isOpen: boolean;
  onClose: () => void;
}

interface ResponseData {
  id: number;
  type: string;
  subject: string;
  question: string;
  answer: string;
  correct: boolean;
  responseTime: number;
  attempts: number;
  confidence: 'low' | 'medium' | 'high';
  timestamp: string;
  tags: string[];
}

const sampleResponses: ResponseData[] = [
  {
    id: 1,
    type: 'Algebra',
    subject: 'Year 8 Maths',
    question: 'Solve for x: 3(x + 4) = 2x + 15',
    answer: 'x = 3',
    correct: true,
    responseTime: 18.2,
    attempts: 1,
    confidence: 'high',
    timestamp: '2 min ago',
    tags: ['Linear Equations', 'Distributive Property', 'Problem Solving'],
  },
  {
    id: 2,
    type: 'Fractions',
    subject: 'Year 6 Maths',
    question: 'Calculate: ¾ ÷ ⅔ + ½',
    answer: '1⅝',
    correct: true,
    responseTime: 24.7,
    attempts: 2,
    confidence: 'medium',
    timestamp: '5 min ago',
    tags: ['Division of Fractions', 'Mixed Operations', 'Order of Operations'],
  },
  {
    id: 3,
    type: 'Geometry',
    subject: 'Year 9 Maths',
    question: 'Find the area of a triangle with vertices at (0,0), (4,0), (2,5)',
    answer: '10 units²',
    correct: false,
    responseTime: 45.3,
    attempts: 3,
    confidence: 'low',
    timestamp: '9 min ago',
    tags: ['Coordinate Geometry', 'Area Formula', 'Vertices'],
  },
  {
    id: 4,
    type: 'Probability',
    subject: 'Year 7 Maths',
    question: 'A bag contains 4 red, 3 blue, 2 green balls. P(red or blue)?',
    answer: '7/9',
    correct: true,
    responseTime: 31.5,
    attempts: 1,
    confidence: 'high',
    timestamp: '14 min ago',
    tags: ['Combined Events', 'Probability Notation', 'Sample Space'],
  },
  {
    id: 5,
    type: 'Algebra',
    subject: 'Year 10 Maths',
    question: 'Factorise: 2x² + 7x + 3',
    answer: '(2x + 1)(x + 3)',
    correct: true,
    responseTime: 22.8,
    attempts: 2,
    confidence: 'medium',
    timestamp: '18 min ago',
    tags: ['Quadratic Expressions', 'Factorisation', 'Trinomials'],
  },
];

const getConfidenceColor = (confidence: string) => {
  switch (confidence) {
    case 'high': return '#10b981';
    case 'medium': return '#f59e0b';
    case 'low': return '#f43f5e';
    default: return '#94a3b8';
  }
};

const DataCollectionModal: React.FC<DataCollectionModalProps> = ({ isOpen, onClose }) => {
  const [activeTab, setActiveTab] = useState<'live' | 'metrics' | 'outcomes'>('live');
  const [animatedValue, setAnimatedValue] = useState(0);
  const [expandedResponses, setExpandedResponses] = useState<Set<number>>(new Set([1]));

  const toggleResponse = (id: number) => {
    setExpandedResponses((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      // Animate metrics on open
      const timer = setInterval(() => {
        setAnimatedValue((prev) => Math.min(prev + 2, 100));
      }, 20);
      return () => {
        clearInterval(timer);
        document.body.style.overflow = '';
      };
    }
  }, [isOpen]);

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) onClose();
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const masteryScore = Math.round(animatedValue * 0.87);
  const questionFrequency = Math.round(animatedValue * 0.65);
  const retentionRate = Math.round(animatedValue * 0.92);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0"
        style={{ background: 'rgba(10, 14, 39, 0.9)', backdropFilter: 'blur(8px)' }}
        onClick={onClose}
      />

      {/* Modal */}
      <div
        className="relative w-full max-w-5xl max-h-[90vh] m-4 overflow-hidden flex flex-col"
        style={{
          background: 'linear-gradient(180deg, rgba(30, 41, 59, 0.95) 0%, rgba(15, 23, 42, 0.98) 100%)',
          borderRadius: '16px',
          border: '1px solid rgba(255, 255, 255, 0.1)',
          boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5)',
        }}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b" style={{ borderColor: 'rgba(255, 255, 255, 0.1)' }}>
          <div>
            <h2
              className="text-xl font-bold"
              style={{
                background: 'linear-gradient(135deg, #10b981 0%, #06b6d4 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
              }}
            >
              Data Collection Engine
            </h2>
            <p className="text-sm" style={{ color: '#94a3b8' }}>
              Every interaction builds the knowledge profile
            </p>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg transition-colors hover:bg-white/10" style={{ color: '#94a3b8' }}>
            <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 px-6 py-3 border-b" style={{ borderColor: 'rgba(255, 255, 255, 0.05)' }}>
          {[
            { id: 'live', label: 'Live Feed', icon: '◉' },
            { id: 'metrics', label: 'Collected Metrics', icon: '◆' },
            { id: 'outcomes', label: 'System Outcomes', icon: '→' },
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id as 'live' | 'metrics' | 'outcomes')}
              className="px-4 py-2 rounded-lg text-sm font-medium transition-all"
              style={{
                background: activeTab === tab.id ? 'rgba(16, 185, 129, 0.15)' : 'transparent',
                color: activeTab === tab.id ? '#10b981' : '#94a3b8',
                border: activeTab === tab.id ? '1px solid rgba(16, 185, 129, 0.3)' : '1px solid transparent',
              }}
            >
              <span className="mr-2">{tab.icon}</span>
              {tab.label}
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-auto p-6">
          {activeTab === 'live' && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Response Feed */}
              <div
                className="rounded-xl p-4"
                style={{ background: 'rgba(255, 255, 255, 0.02)', border: '1px solid rgba(255, 255, 255, 0.05)' }}
              >
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-semibold" style={{ color: '#e2e8f0' }}>Response Stream</h3>
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 rounded-full animate-pulse" style={{ background: '#10b981' }} />
                    <span className="text-xs" style={{ color: '#10b981' }}>Live</span>
                  </div>
                </div>
                <div className="space-y-2 max-h-[400px] overflow-y-auto" style={{ scrollbarWidth: 'thin' }}>
                  {sampleResponses.map((response, index) => {
                    const isExpanded = expandedResponses.has(response.id);
                    return (
                      <div
                        key={response.id}
                        className="rounded-lg transition-all overflow-hidden"
                        style={{
                          background: index === 0 ? 'rgba(16, 185, 129, 0.08)' : 'rgba(255, 255, 255, 0.02)',
                          border: `1px solid ${index === 0 ? 'rgba(16, 185, 129, 0.2)' : 'rgba(255, 255, 255, 0.05)'}`,
                        }}
                      >
                        {/* Collapsed Header - Always Visible */}
                        <div
                          className="p-3 flex items-center justify-between cursor-pointer hover:bg-white/5 transition-colors"
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleResponse(response.id);
                          }}
                        >
                          <div className="flex items-center gap-2 flex-1 min-w-0">
                            <div
                              className="w-6 h-6 rounded flex items-center justify-center text-xs font-bold flex-shrink-0"
                              style={{
                                background: response.correct ? 'rgba(16, 185, 129, 0.2)' : 'rgba(244, 63, 94, 0.2)',
                                color: response.correct ? '#10b981' : '#f43f5e',
                              }}
                            >
                              {response.correct ? '✓' : '✗'}
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 mb-0.5">
                                <span
                                  className="text-[10px] px-1.5 py-0.5 rounded font-medium"
                                  style={{ background: 'rgba(14, 165, 233, 0.2)', color: '#0ea5e9' }}
                                >
                                  {response.type}
                                </span>
                                <span className="text-[10px]" style={{ color: '#64748b' }}>
                                  {response.subject}
                                </span>
                                <span className="text-[10px] ml-auto" style={{ color: '#64748b' }}>
                                  {response.timestamp}
                                </span>
                              </div>
                              <p className="text-sm truncate" style={{ color: '#e2e8f0' }}>
                                {response.question}
                              </p>
                            </div>
                          </div>
                          <div className="flex-shrink-0 ml-2">
                            {isExpanded ? (
                              <ChevronUpIcon className="w-4 h-4" style={{ color: '#64748b' }} />
                            ) : (
                              <ChevronDownIcon className="w-4 h-4" style={{ color: '#64748b' }} />
                            )}
                          </div>
                        </div>

                        {/* Expanded Details */}
                        {isExpanded && (
                          <div
                            className="px-3 pb-3 space-y-3"
                            style={{ background: 'rgba(0, 0, 0, 0.1)' }}
                          >
                            {/* Answer */}
                            <div className="flex items-center gap-2">
                              <span className="text-xs" style={{ color: '#64748b' }}>Answer:</span>
                              <span
                                className="text-sm font-mono px-2 py-0.5 rounded"
                                style={{
                                  background: response.correct ? 'rgba(16, 185, 129, 0.15)' : 'rgba(244, 63, 94, 0.15)',
                                  color: response.correct ? '#10b981' : '#f43f5e',
                                }}
                              >
                                {response.answer}
                              </span>
                            </div>

                            {/* Metrics Row */}
                            <div className="flex items-center gap-4 text-xs">
                              <span style={{ color: '#64748b' }}>
                                ⏱ {response.responseTime}s
                              </span>
                              <span style={{ color: '#64748b' }}>
                                ↻ {response.attempts} {response.attempts === 1 ? 'attempt' : 'attempts'}
                              </span>
                              <span style={{ color: getConfidenceColor(response.confidence) }}>
                                ◆ {response.confidence}
                              </span>
                            </div>

                            {/* Tags */}
                            <div className="flex flex-wrap gap-1.5">
                              {response.tags.map((tag) => (
                                <span
                                  key={tag}
                                  className="text-[10px] px-2 py-0.5 rounded"
                                  style={{
                                    background: 'rgba(139, 92, 246, 0.15)',
                                    color: '#a78bfa',
                                  }}
                                >
                                  {tag}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Data Extraction Panel */}
              <div
                className="rounded-xl p-4"
                style={{ background: 'rgba(255, 255, 255, 0.02)', border: '1px solid rgba(255, 255, 255, 0.05)' }}
              >
                <h3 className="font-semibold mb-4" style={{ color: '#e2e8f0' }}>Data Points Extracted</h3>
                <div className="space-y-4">
                  {[
                    { label: 'Response Accuracy', value: 'Correct/Incorrect', description: 'Binary outcome for each question', color: '#10b981' },
                    { label: 'Response Time', value: '2.3 seconds avg', description: 'Time from question display to submission', color: '#0ea5e9' },
                    { label: 'Attempt Count', value: '1.4 attempts avg', description: 'Number of tries before correct answer', color: '#f59e0b' },
                    { label: 'Hint Usage', value: '23% of questions', description: 'Whether hints were requested', color: '#8b5cf6' },
                    { label: 'Time of Day', value: '3:45 PM', description: 'When the response was submitted', color: '#f43f5e' },
                    { label: 'Session Duration', value: '12 minutes', description: 'Total active learning time', color: '#06b6d4' },
                  ].map((item) => (
                    <div key={item.label} className="flex items-start gap-3">
                      <div className="w-2 h-2 rounded-full mt-1.5" style={{ background: item.color }} />
                      <div className="flex-1">
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-medium" style={{ color: '#e2e8f0' }}>{item.label}</span>
                          <span className="text-sm" style={{ color: item.color }}>{item.value}</span>
                        </div>
                        <span className="text-xs" style={{ color: '#64748b' }}>{item.description}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {activeTab === 'metrics' && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {/* Mastery Score */}
              <div
                className="rounded-xl p-6 text-center"
                style={{ background: 'rgba(16, 185, 129, 0.05)', border: '1px solid rgba(16, 185, 129, 0.2)' }}
              >
                <h3 className="text-sm font-medium mb-4" style={{ color: '#94a3b8' }}>Module Mastery</h3>
                <div className="relative w-32 h-32 mx-auto mb-4">
                  <svg className="w-full h-full transform -rotate-90">
                    <circle cx="64" cy="64" r="56" fill="none" stroke="rgba(255,255,255,0.1)" strokeWidth="8" />
                    <circle
                      cx="64"
                      cy="64"
                      r="56"
                      fill="none"
                      stroke="#10b981"
                      strokeWidth="8"
                      strokeLinecap="round"
                      strokeDasharray={`${masteryScore * 3.52} 352`}
                      style={{ transition: 'stroke-dasharray 0.5s ease' }}
                    />
                  </svg>
                  <div className="absolute inset-0 flex items-center justify-center">
                    <span className="text-3xl font-bold" style={{ color: '#10b981' }}>{masteryScore}%</span>
                  </div>
                </div>
                <p className="text-xs" style={{ color: '#64748b' }}>Based on accuracy, speed & consistency</p>
              </div>

              {/* Question Frequency */}
              <div
                className="rounded-xl p-6 text-center"
                style={{ background: 'rgba(14, 165, 233, 0.05)', border: '1px solid rgba(14, 165, 233, 0.2)' }}
              >
                <h3 className="text-sm font-medium mb-4" style={{ color: '#94a3b8' }}>Review Frequency</h3>
                <div className="flex items-end justify-center gap-2 h-32 mb-4">
                  {[40, 65, 45, 80, 60, 70, questionFrequency].map((height, i) => (
                    <div
                      key={i}
                      className="w-6 rounded-t transition-all duration-500"
                      style={{
                        height: `${height}%`,
                        background: i === 6 ? '#0ea5e9' : 'rgba(14, 165, 233, 0.3)',
                      }}
                    />
                  ))}
                </div>
                <p className="text-xs" style={{ color: '#64748b' }}>Spaced repetition intervals adjust automatically</p>
              </div>

              {/* Retention Rate */}
              <div
                className="rounded-xl p-6 text-center"
                style={{ background: 'rgba(139, 92, 246, 0.05)', border: '1px solid rgba(139, 92, 246, 0.2)' }}
              >
                <h3 className="text-sm font-medium mb-4" style={{ color: '#94a3b8' }}>Retention Rate</h3>
                <div className="relative w-32 h-32 mx-auto mb-4">
                  <svg className="w-full h-full transform -rotate-90">
                    <circle cx="64" cy="64" r="56" fill="none" stroke="rgba(255,255,255,0.1)" strokeWidth="8" />
                    <circle
                      cx="64"
                      cy="64"
                      r="56"
                      fill="none"
                      stroke="#8b5cf6"
                      strokeWidth="8"
                      strokeLinecap="round"
                      strokeDasharray={`${retentionRate * 3.52} 352`}
                      style={{ transition: 'stroke-dasharray 0.5s ease' }}
                    />
                  </svg>
                  <div className="absolute inset-0 flex items-center justify-center">
                    <span className="text-3xl font-bold" style={{ color: '#8b5cf6' }}>{retentionRate}%</span>
                  </div>
                </div>
                <p className="text-xs" style={{ color: '#64748b' }}>Knowledge retained after 7 days</p>
              </div>
            </div>
          )}

          {activeTab === 'outcomes' && (
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* How Data Flows */}
                <div
                  className="rounded-xl p-6"
                  style={{ background: 'rgba(255, 255, 255, 0.02)', border: '1px solid rgba(255, 255, 255, 0.05)' }}
                >
                  <h3 className="font-semibold mb-4" style={{ color: '#e2e8f0' }}>Data → Decision Pipeline</h3>
                  <div className="space-y-4">
                    {[
                      { from: 'Student Response', to: 'Accuracy Score', icon: '→', color: '#10b981' },
                      { from: 'Response Time', to: 'Confidence Level', icon: '→', color: '#0ea5e9' },
                      { from: 'Attempt Count', to: 'Difficulty Rating', icon: '→', color: '#f59e0b' },
                      { from: 'Pattern Analysis', to: 'Next Question', icon: '→', color: '#8b5cf6' },
                    ].map((flow, index) => (
                      <div key={index} className="flex items-center gap-3">
                        <div className="flex-1 text-right">
                          <span className="text-sm" style={{ color: '#94a3b8' }}>{flow.from}</span>
                        </div>
                        <div className="w-8 h-8 rounded-full flex items-center justify-center" style={{ background: `${flow.color}20` }}>
                          <span style={{ color: flow.color }}>{flow.icon}</span>
                        </div>
                        <div className="flex-1">
                          <span className="text-sm font-medium" style={{ color: flow.color }}>{flow.to}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* System Actions */}
                <div
                  className="rounded-xl p-6"
                  style={{ background: 'rgba(255, 255, 255, 0.02)', border: '1px solid rgba(255, 255, 255, 0.05)' }}
                >
                  <h3 className="font-semibold mb-4" style={{ color: '#e2e8f0' }}>Automated Actions</h3>
                  <div className="space-y-3">
                    {[
                      { action: 'Adjust question difficulty', trigger: 'After 3+ incorrect attempts', color: '#f59e0b' },
                      { action: 'Schedule review session', trigger: 'Based on forgetting curve', color: '#8b5cf6' },
                      { action: 'Unlock next module', trigger: 'When mastery > 80%', color: '#10b981' },
                      { action: 'Suggest practice area', trigger: 'Weakness pattern detected', color: '#f43f5e' },
                      { action: 'Update teacher dashboard', trigger: 'Real-time sync', color: '#0ea5e9' },
                    ].map((item, index) => (
                      <div
                        key={index}
                        className="p-3 rounded-lg"
                        style={{ background: 'rgba(255, 255, 255, 0.02)' }}
                      >
                        <div className="flex items-center gap-2 mb-1">
                          <div className="w-2 h-2 rounded-full" style={{ background: item.color }} />
                          <span className="text-sm font-medium" style={{ color: '#e2e8f0' }}>{item.action}</span>
                        </div>
                        <span className="text-xs ml-4" style={{ color: '#64748b' }}>{item.trigger}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div
          className="px-6 py-3 border-t flex items-center justify-between"
          style={{ borderColor: 'rgba(255, 255, 255, 0.1)' }}
        >
          <p className="text-xs" style={{ color: '#64748b' }}>
            All data is encrypted and processed in real-time to personalise learning paths.
          </p>
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 rounded-full animate-pulse" style={{ background: '#10b981' }} />
            <span className="text-xs" style={{ color: '#10b981' }}>System Active</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DataCollectionModal;
